import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import {
  type CreateClassificationFeedbackV1ExploreImagesExploreImageIdFeedbackPostData,
  type CreateClassificationFeedbackV1ExploreImagesExploreImageIdFeedbackPostError,
  type CreateClassificationFeedbackV1ExploreImagesExploreImageIdFeedbackPostResponse,
  createClassificationFeedbackV1ExploreImagesExploreImageIdFeedbackPost,
} from '../../generated';

export type CreateClassificationFeedbackMutationVariables =
  CreateClassificationFeedbackV1ExploreImagesExploreImageIdFeedbackPostData['path'] &
    CreateClassificationFeedbackV1ExploreImagesExploreImageIdFeedbackPostData['body'];

export type CreateClassificationFeedbackMutation =
  CreateClassificationFeedbackV1ExploreImagesExploreImageIdFeedbackPostResponse;

export const useCreateClassificationFeedbackMutation = <
  TError = CreateClassificationFeedbackV1ExploreImagesExploreImageIdFeedbackPostError,
  TContext = unknown,
>(
  options?: Omit<
    UseMutationOptions<
      CreateClassificationFeedbackMutation,
      TError,
      CreateClassificationFeedbackMutationVariables,
      TContext
    >,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation<
    CreateClassificationFeedbackMutation,
    TError,
    CreateClassificationFeedbackMutationVariables,
    TContext
  >({
    mutationKey: ['CreateClassificationFeedback'],
    mutationFn: async ({ explore_image_id, ...body }: CreateClassificationFeedbackMutationVariables) => {
      const { data } = await createClassificationFeedbackV1ExploreImagesExploreImageIdFeedbackPost({
        path: { explore_image_id },
        body,
        throwOnError: true,
      });
      return data;
    },
    ...options,
  });
};

useCreateClassificationFeedbackMutation.fetcher = (
  { explore_image_id, ...body }: CreateClassificationFeedbackMutationVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data } = await createClassificationFeedbackV1ExploreImagesExploreImageIdFeedbackPost({
      path: { explore_image_id },
      body,
      throwOnError: true,
    });
    return data;
  };
};
